import { ALL_ROLES, normalizeRole, ROLE_DESCRIPTIONS, ROLE_LABELS, ROLES } from "../config/roles";
import {
  ALL_OPS_PERMISSIONS,
  getPermissionsForRole as getDefaultPermissionsForRole,
} from "../config/permissions";

function cloneDefaults() {
  return Object.fromEntries(ALL_ROLES.map((role) => [role, [...getDefaultPermissionsForRole(role)]]));
}

function cloneMeta() {
  return Object.fromEntries(
    ALL_ROLES.map((role) => [
      role,
      {
        label: ROLE_LABELS[role],
        description: ROLE_DESCRIPTIONS[role] ?? "",
        system: true,
        locked: role === ROLES.SUPER_ADMIN,
        role_slug: null,
        provider: false,
      },
    ])
  );
}

function slugify(label) {
  const base = String(label ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
  return base || `custom_role_${Date.now()}`;
}

let roleOrder = [...ALL_ROLES];
let rolePermissions = cloneDefaults();
let roleMeta = cloneMeta();
let rolesSource = "local"; // "local" | "api"
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => fn());
}

export function subscribeRolePermissions(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function listRoles() {
  return [...roleOrder];
}

export function getRolesSource() {
  return rolesSource;
}

/** Roles that can be assigned when inviting / managing team members. */
export function listAssignableRoles({ actorRole } = {}) {
  const actor = normalizeRole(actorRole);
  const allowed = {
    [ROLES.SUPER_ADMIN]: [
      ROLES.SUPER_ADMIN,
      ROLES.EXECUTIVE_MANAGER,
      ROLES.CALL_CENTER_MANAGER,
      ROLES.CALL_CENTER_AGENT,
      ROLES.LEGAL_OFFICER,
    ],
    [ROLES.EXECUTIVE_MANAGER]: [
      ROLES.CALL_CENTER_MANAGER,
      ROLES.CALL_CENTER_AGENT,
      ROLES.LEGAL_OFFICER,
    ],
    [ROLES.CALL_CENTER_MANAGER]: [ROLES.CALL_CENTER_AGENT],
  };

  return (allowed[actor] || []).filter((role) => !roleMeta[role]?.provider);
}

export function isRoleLocked(role) {
  return role === ROLES.SUPER_ADMIN || Boolean(roleMeta[role]?.locked);
}

export function isSystemRole(role) {
  return Boolean(roleMeta[role]?.system);
}

export function getRolePermissions(role) {
  if (rolePermissions[role]) return [...rolePermissions[role]];
  if (rolesSource === "api") return [];
  if (role === ROLES.SUPER_ADMIN) return [...ALL_OPS_PERMISSIONS];
  return [...(getDefaultPermissionsForRole(role) ?? [])];
}

export function getRoleMeta(role) {
  return (
    roleMeta[role] ?? {
      label: ROLE_LABELS[role] ?? role,
      description: ROLE_DESCRIPTIONS[role] ?? "",
      system: false,
      locked: false,
      role_slug: null,
      provider: false,
    }
  );
}

/** Backend UUID for create/update/delete. Never fall back to role name on live API. */
export function getRoleApiSlug(role) {
  const slug = roleMeta[role]?.role_slug;
  if (slug) return slug;
  // Mock / local roles: name key is acceptable.
  if (rolesSource !== "api") return role || null;
  return null;
}

/**
 * Map a stored admin.role_slug (UUID or legacy role name) → store role key (name).
 */
export function resolveRoleKeyFromSlug(roleSlugOrName) {
  if (!roleSlugOrName) return null;
  const value = String(roleSlugOrName);
  if (roleOrder.includes(value) || roleMeta[value]) return value;
  const bySlug = Object.entries(roleMeta).find(([, meta]) => meta?.role_slug === value);
  if (bySlug) return bySlug[0];
  return value;
}

export function getRoleDisplayLabel(role) {
  return getRoleMeta(role).label;
}

export function getRoleDisplayDescription(role) {
  return getRoleMeta(role).description;
}

export function setRoleMeta(role, { label, description }) {
  if (isRoleLocked(role)) return getRoleMeta(role);
  const current = getRoleMeta(role);
  roleMeta = {
    ...roleMeta,
    [role]: {
      ...current,
      label: label?.trim() || current.label,
      description: description !== undefined ? description : current.description,
    },
  };
  notify();
  return getRoleMeta(role);
}

export function setRolePermissions(role, permissions) {
  if (isRoleLocked(role)) {
    return getRolePermissions(role);
  }
  rolePermissions = { ...rolePermissions, [role]: [...permissions] };
  notify();
  return getRolePermissions(role);
}

export function resetRolePermissions(role) {
  if (isRoleLocked(role)) return getRolePermissions(role);
  if (rolesSource === "api") {
    // Keep current API permissions — caller should reload from API for a true reset.
    return getRolePermissions(role);
  }
  if (isSystemRole(role)) {
    roleMeta = {
      ...roleMeta,
      [role]: {
        ...getRoleMeta(role),
        label: ROLE_LABELS[role] ?? getRoleMeta(role).label,
        description: ROLE_DESCRIPTIONS[role] ?? "",
      },
    };
    return setRolePermissions(role, getDefaultPermissionsForRole(role));
  }
  return setRolePermissions(role, []);
}

/** Clear local defaults before an API hydrate (avoids flashing mock roles). */
export function clearRolesForApiLoad() {
  roleOrder = [];
  rolePermissions = {};
  roleMeta = {};
  rolesSource = "api";
  notify();
}

/**
 * Replace local role state from GET /api/ops/all_roles.
 * Keys remain role `name`; UUID lives in meta.role_slug.
 * Permissions come from the API as-is (no FE template override).
 */
export function hydrateRolesFromApi(normalizedRoles) {
  const list = (normalizedRoles ?? []).filter(Boolean);
  rolesSource = "api";

  roleOrder = list.map((role) => role.name);
  rolePermissions = Object.fromEntries(
    list.map((role) => [role.name, [...(role.permissions ?? [])]])
  );
  roleMeta = Object.fromEntries(
    list.map((role) => [
      role.name,
      {
        label: role.label || ROLE_LABELS[role.name] || role.name,
        description: ROLE_DESCRIPTIONS[role.name] ?? "",
        system: Boolean(role.system),
        locked: Boolean(role.locked) || role.name === ROLES.SUPER_ADMIN,
        role_slug: role.role_slug ?? null,
        provider: Boolean(role.provider),
      },
    ])
  );
  notify();
}

/**
 * Insert or replace a role after create/update. Renames the store key when `name` changes.
 */
export function upsertRoleFromApi(normalized, { previousName } = {}) {
  if (!normalized?.name) return null;
  rolesSource = "api";

  const key = normalized.name;
  const prev = previousName && previousName !== key ? previousName : null;

  if (prev) {
    roleOrder = roleOrder.map((item) => (item === prev ? key : item));
    const { [prev]: _meta, ...restMeta } = roleMeta;
    const { [prev]: _perms, ...restPerms } = rolePermissions;
    roleMeta = restMeta;
    rolePermissions = restPerms;
  } else if (!roleOrder.includes(key)) {
    roleOrder = [...roleOrder, key];
  }

  roleMeta = {
    ...roleMeta,
    [key]: {
      label: normalized.label || ROLE_LABELS[key] || key,
      description: ROLE_DESCRIPTIONS[key] ?? getRoleMeta(key).description ?? "",
      system: Boolean(normalized.system),
      locked: Boolean(normalized.locked) || key === ROLES.SUPER_ADMIN,
      role_slug: normalized.role_slug ?? getRoleMeta(key).role_slug ?? null,
      provider: Boolean(normalized.provider),
    },
  };
  rolePermissions = {
    ...rolePermissions,
    [key]: [...(normalized.permissions ?? [])],
  };
  notify();
  return key;
}

export function removeRoleFromStore(role) {
  if (!roleOrder.includes(role)) return false;
  if (isRoleLocked(role)) return false;
  roleOrder = roleOrder.filter((item) => item !== role);
  const { [role]: _meta, ...restMeta } = roleMeta;
  const { [role]: _perms, ...restPerms } = rolePermissions;
  roleMeta = restMeta;
  rolePermissions = restPerms;
  notify();
  return true;
}

/**
 * Create a custom role locally (mock / offline). Returns the new role name key.
 */
export function createCustomRole({ label, description = "", permissions = [] }) {
  let slug = slugify(label);
  if (roleOrder.includes(slug) || slug === ROLES.SUPER_ADMIN) {
    slug = `${slug}_${Date.now().toString(36)}`;
  }

  roleOrder = [...roleOrder, slug];
  roleMeta = {
    ...roleMeta,
    [slug]: {
      label: label.trim(),
      description: description.trim(),
      system: false,
      locked: false,
      role_slug: null,
      provider: false,
    },
  };
  rolePermissions = {
    ...rolePermissions,
    [slug]: [...permissions],
  };
  notify();
  return slug;
}

export function deleteCustomRole(role) {
  return removeRoleFromStore(role);
}

/** Restore built-in local templates (mock auth). */
export function resetRolesToLocalDefaults() {
  roleOrder = [...ALL_ROLES];
  rolePermissions = cloneDefaults();
  roleMeta = cloneMeta();
  rolesSource = "local";
  notify();
}
